/**
 * Graceful "you don't have permission" placeholder (Gap 1).
 *
 * Rendered in place of a widget/page a sub-delegate cannot access, instead of
 * a raw 403 error message. Two variants:
 *  - `page`    — large centered card for a whole-page gate.
 *  - `widget`  — compact inline card for a dashboard section/widget slot.
 */

import { Lock } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

type PermissionDeniedCardProps = {
  /** Optional feature name shown in the message, e.g. "Messages". */
  feature?: string;
  variant?: 'page' | 'widget';
  className?: string;
};

const DEFAULT_TITLE = 'You do not have permission to access this feature';

export function PermissionDeniedCard({
  feature,
  variant = 'widget',
  className,
}: PermissionDeniedCardProps) {
  const title = feature
    ? `You do not have permission to access ${feature}`
    : DEFAULT_TITLE;

  const isPage = variant === 'page';

  return (
    <div
      role="status"
      data-testid="permission-denied-card"
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-card text-center text-card-foreground',
        isPage ? 'min-h-[50vh] gap-3 p-10' : 'gap-2 p-6',
        className,
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-full bg-[#026857]/10 text-[#026857]',
          isPage ? 'h-12 w-12' : 'h-10 w-10',
        )}
      >
        <Lock className={isPage ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
      </span>
      <p className={cn('font-semibold', isPage ? 'text-lg' : 'text-sm')}>{title}</p>
      <p className={cn('text-muted-foreground', isPage ? 'text-sm' : 'text-xs')}>
        Ask the workspace owner to grant you access.
      </p>
    </div>
  );
}
